import React, { useCallback, useContext, useMemo, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation, Trans } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import styles from "./AttendanceList.module.css";
import { IAssemblyAttendances, IAttendanceState } from "src/stores/assemblies/AttendanceListStore";
import { ScreenSize, UI } from "@wwimmo/ui";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { AttendanceListRowDesktop } from "./AttendanceListRowDesktop";
import { AttendanceListRowMobile } from "./AttendanceListRowMobile";
import { AttendanceQuorumInfo } from "src/screens/assemblies/assembly/attendance/AttendanceQuorumInfo";
import { ErpType } from "src/network/User";
import { v4 as uuidv4 } from "uuid";

interface IAttendanceListProps {
    assemblyid: string;
    isLiveModeSmallView: boolean;
}

const AttendanceDesktopTabsBase = ({
    assemblyid,
    isLiveModeSmallView
}: {
    assemblyid: string;
    isLiveModeSmallView: boolean;
}) => {
    const { attendanceListStore, uiStore, assemblyStore, authStore } = useContext(RootStoreContext);

    const isIT2 = authStore.user?.erpType === ErpType.IT2;

    const onTabsChange = useCallback(
        (realestateId: string) => {
            if (realestateId !== attendanceListStore.selectedRealestateId) {
                attendanceListStore.setSelectedRealestateId(realestateId);
                if (assemblyid && realestateId) {
                    attendanceListStore.loadAttendanceByRealestateId(
                        assemblyid,
                        attendanceListStore.selectedRealestateId
                    );
                }
            }
        },
        [assemblyid, attendanceListStore]
    );

    const tabContents = useMemo(() => {
        const assembly = assemblyStore.currentAssembly;
        if (!assembly) return [];

        const items = assembly.assemblyrealestates ?? [];

        // If not IT2: return all items. If IT2: only items with a non-empty consolidationid.
        const filtered = !isIT2
            ? items
            : items.filter((ar) => {
                  const cid = ar.realestate?.consolidationid;
                  return cid != null && String(cid).trim() !== "";
              });

        return filtered
            .map((ar) => ({
                label: ar.realestate?.name ?? "",
                id: ar.realestate?.id ?? ""
            }))
            .filter((t) => t.id); // keep only valid ids
    }, [assemblyStore.currentAssembly, isIT2]);

    // Keep selected ID valid after filtering
    useEffect(() => {
        const selected = attendanceListStore.selectedRealestateId;
        const hasSelected = tabContents.some((tab) => tab.id === selected);

        if (!selected || !hasSelected) {
            if (tabContents.length >= 1) {
                onTabsChange(tabContents[0].id);
            }
        }
    }, [tabContents, attendanceListStore.selectedRealestateId, onTabsChange]);

    if (uiStore.isMobile || isLiveModeSmallView) {
        return null;
    }

    return (
        <div className={styles.Tabbar}>
            {tabContents.length <= 1 ? null : (
                <UI.Tabs
                    selectedId={attendanceListStore.selectedRealestateId || undefined}
                    onTabChange={onTabsChange}
                    contents={tabContents}
                    nowrap
                />
            )}
        </div>
    );
};

const AttendanceDesktopTabs = observer(AttendanceDesktopTabsBase);

const AttendanceListRendererBase = (props: IAttendanceListProps) => {
    const { t } = useTranslation();
    const { attendanceListStore, uiStore, assemblyStore } = useContext(RootStoreContext);
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const columnWidths = useMemo(() => {
        const columnWidthsDesktop = [1, 0.5, 1, 0.5, 0.5];
        const columnWidthsLaptopL = [0.8, 0.3, 0.8, 0.3, 0.3];
        const columnWidthsLaptop = [0.6, 0.3, 0.7, 0.2, 0.2];
        const columnWidthsTablet = [0.4, 0.1, 0.5, 0.1, 0.1];

        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
              ? columnWidthsLaptopL
              : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
                ? columnWidthsLaptop
                : columnWidthsTablet;
    }, [uiStore.currentScreenSize]);

    const renderItem = useCallback(
        (attendance: IAssemblyAttendances, screenSize: ScreenSize) => {
            const isFirstTenant = attendance.isFirstTenant;

            if (screenSize === ScreenSize.MOBILE && !isFirstTenant) {
                return <React.Fragment />;
            }

            return (
                <UI.List.Item
                    key={`item-${uuidv4()}`}
                    screenSize={screenSize}
                    rows={[
                        screenSize === ScreenSize.MOBILE ? (
                            <AttendanceListRowMobile attendances={attendance} key={`row-${attendance.unitname}`} />
                        ) : (
                            <AttendanceListRowDesktop
                                attendances={attendance}
                                key={`row-${attendance.unitname}`}
                                widths={columnWidths}
                                showTenantName={isFirstTenant}
                            />
                        )
                    ]}
                    className={isFirstTenant ? styles.ListItemWithoutLine : styles.ListItem}
                />
            );
        },

        [columnWidths]
    );

    const onCheckboxChangeAllState = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newState = e.target.checked ? IAttendanceState.PRESENT : IAttendanceState.ABSENT;
            attendanceListStore.updateAllAttendanceStates(newState);
        },
        [attendanceListStore]
    );

    const onCheckboxChangeAllTenantChecked = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            attendanceListStore.updateAllTenantChecked(e.target.checked);
        },
        [attendanceListStore]
    );

    const onCheckboxChangeAllUnitChecked = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            attendanceListStore.updateAllUnitChecked(e.target.checked);
        },
        [attendanceListStore]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            const mobileRows = [
                <UI.List.Cell
                    key="tenantname"
                    colspan={1}
                    value={<Trans>screens.assemblies.attendance.tenantname</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key="status"
                    colspan={0.5}
                    value={<Trans>screens.assemblies.attendance.state</Trans>}
                    truncate
                />
            ];

            const desktopRowsPresent = [
                <UI.List.Cell
                    key="tenantname"
                    colspan={columnWidths[0]}
                    value={<Trans>screens.assemblies.attendance.tenantname</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key="valueQuota"
                    colspan={columnWidths[1]}
                    value={<Trans>screens.assemblies.attendance.value_quota</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key="unitname"
                    colspan={columnWidths[2]}
                    value={<Trans>screens.assemblies.attendance.unitname</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    className={styles.CheckboxState}
                    key="status-present"
                    colspan={columnWidths[3]}
                    value={
                        <div>
                            <Trans>screens.assemblies.attendance.present</Trans>
                            <UI.Checkbox
                                className={styles.CheckboxPresentHeader}
                                name="attendance-all"
                                checked={attendanceListStore.areAllPresent}
                                onChange={onCheckboxChangeAllState}
                                disabled={isReadOnly}
                            />
                        </div>
                    }
                    truncate
                />,
                <UI.List.Cell
                    className={styles.CheckboxState}
                    key="status-represented"
                    colspan={columnWidths[4]}
                    value={<Trans>screens.assemblies.attendance.represented</Trans>}
                    truncate
                />
            ];

            const desktopRowsVotes = [
                <UI.List.Cell
                    className={styles.CheckboxState}
                    key="tenantvalue"
                    colspan={0.2}
                    value={
                        <div>
                            <Trans>screens.assemblies.attendance.tenantvalue</Trans>
                            <UI.Checkbox
                                className={styles.CheckboxPresentHeader}
                                name="attendance-all-tenant"
                                checked={attendanceListStore.areAllTenantChecked}
                                onChange={onCheckboxChangeAllTenantChecked}
                                disabled={isReadOnly}
                            />
                        </div>
                    }
                    truncate
                />,
                <UI.List.Cell
                    key="tenantname"
                    colspan={0.6}
                    value={<Trans>screens.assemblies.attendance.tenantname</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key="valueQuota"
                    colspan={0.3}
                    value={<Trans>screens.assemblies.attendance.value_quota</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    className={styles.CheckboxState}
                    key="unitvalue"
                    colspan={0.2}
                    value={
                        <div>
                            <Trans>screens.assemblies.attendance.unitvalue</Trans>
                            <UI.Checkbox
                                className={styles.CheckboxPresentHeader}
                                name="attendance-all-unit"
                                checked={attendanceListStore.areAllUnitChecked}
                                onChange={onCheckboxChangeAllUnitChecked}
                                disabled={isReadOnly}
                            />
                        </div>
                    }
                    truncate
                />,
                <UI.List.Cell
                    key="unitname"
                    colspan={columnWidths[2]}
                    value={<Trans>screens.assemblies.attendance.unitname</Trans>}
                    truncate
                />
            ];

            const rows =
                screenSize === ScreenSize.MOBILE
                    ? mobileRows
                    : attendanceListStore.isListViewPresent
                      ? desktopRowsPresent
                      : desktopRowsVotes;

            return <UI.List.Header className={styles.AttendanceListHeader + " p-0"} rows={rows} />;
        },
        [
            columnWidths,
            onCheckboxChangeAllState,
            onCheckboxChangeAllTenantChecked,
            onCheckboxChangeAllUnitChecked,
            attendanceListStore.areAllPresent,
            attendanceListStore.areAllTenantChecked,
            attendanceListStore.areAllUnitChecked,
            attendanceListStore.isListViewPresent,
            isReadOnly
        ]
    );

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            attendanceListStore.setSearchQuery(e.target.value);
        },
        [attendanceListStore]
    );

    const handleViewToggle = useCallback(() => {
        attendanceListStore.setIsListViewPresent(!attendanceListStore.isListViewPresent);
    }, [attendanceListStore]);

    const handlePageChanged = useCallback(
        (currentPage: number) => {
            attendanceListStore.setCurrentPage(currentPage);
        },
        [attendanceListStore]
    );

    return (
        <>
            <UI.Input
                type="search"
                placeholder={t("labels.search").toString()}
                value={attendanceListStore.searchQuery}
                onChange={handleSearchChange}
                className={styles.SearchInput}
            />
            <div className={styles.SwitchRow}>
                <span
                    className={`${styles.SwitchLabel} ${attendanceListStore.isListViewPresent ? styles.SwitchLabelActive : ""}`}
                >
                    {t("screens.assemblies.attendance.presented")}
                </span>
                <UI.Switch checked={!attendanceListStore.isListViewPresent} onToggle={handleViewToggle} />
                <span
                    className={`${styles.SwitchLabel} ${!attendanceListStore.isListViewPresent ? styles.SwitchLabelActive : ""}`}
                >
                    {t("screens.assemblies.attendance.unit_value")}
                </span>
            </div>
            <UI.List.BasicList
                key={attendanceListStore.assemblyAttendances.length}
                screenSize={uiStore.isMobile || props.isLiveModeSmallView ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={
                    attendanceListStore.filteredAssemblyAttendances.length > 0 ||
                    attendanceListStore.searchQuery.length > 0
                        ? attendanceListStore.filteredAssemblyAttendances
                        : attendanceListStore.assemblyAttendances
                }
                renderItem={renderItem}
                renderHeader={renderHeader}
                itemsPerPage={attendanceListStore.itemsPerPage}
                onPageChanged={handlePageChanged}
                emptyListMessage={
                    attendanceListStore.assemblyAttendances.length === 0 ||
                    attendanceListStore.filteredAssemblyAttendances.length === 0
                        ? t("screens.assemblies.attendance.no_attendance_found").toString()
                        : `${t("screens.assemblies.attendance.attendance_loading").toString()}...`
                }
            />
        </>
    );
};
const AttendanceListRenderer = observer(AttendanceListRendererBase);

const AttendanceListBase = (props: IAttendanceListProps) => {
    const { assemblyid } = props;
    const { t } = useTranslation();
    const { attendanceListStore } = useContext(RootStoreContext);

    return (
        <div className={styles.AttendanceList}>
            {attendanceListStore.isActualizingAttendanceList ? (
                <div>
                    <UI.RotatingSpinner size={50} className="mt-2 mb-2" />
                    <div className="text-center">{t("screens.assemblies.attendance.data_will_updated")}</div>
                </div>
            ) : (
                <>
                    <AttendanceDesktopTabs assemblyid={assemblyid} isLiveModeSmallView={props.isLiveModeSmallView} />
                    <AttendanceQuorumInfo />
                    <AttendanceListRenderer assemblyid={assemblyid} isLiveModeSmallView={props.isLiveModeSmallView} />
                </>
            )}
        </div>
    );
};

export const AttendanceList = Sentry.withProfiler(observer(AttendanceListBase));
